'use client'

import React from 'react'
import { ArrowRight, Check, RotateCcw, ShieldCheck } from 'lucide-react'
import { RadarMark } from '@/components/brand/RadarMark'
import { VectorMark, HiveMark } from '@/components/brand/product-marks'

/**
 * Directed Grid diagrams: the infographic set for the pillar post
 * `content/directed-grid-ai-operating-model.mdx`.
 *
 * The figures carry the real product marks (RadarMark, VectorMark, HiveMark,
 * the same 64x64 "one grid, three verbs" grammar the homepage hero instrument
 * animates) so the article's diagrams and the hero read as one system. Marks
 * inherit `currentColor` for their neutral cells and keep the literal brand
 * pink for the verb, so they are correct in both themes without a theme hook.
 *
 * Theming follows the blog-diagram contract in CLAUDE.md §7: muted/50 frame,
 * background cards, border/60 hairlines, foreground / muted-foreground ink,
 * accent washes at /5 + /20. No `dark:` variants, no grey scale classes.
 */

const BLUE = '#008dcc'
const PURPLE = '#8b5cf6'
const PINK = '#F90B8A'
const YELLOW = '#FDC304'
const GREEN = '#6bc95a'

function Frame({
  title,
  subtitle,
  source,
  children,
}: {
  title: string
  subtitle?: string
  source?: string
  children: React.ReactNode
}) {
  return (
    <figure data-blog-layout='narrow' className='my-10 mx-0'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-5 sm:p-7'>
        <div className='mb-5'>
          <span className='block text-sm font-semibold text-foreground leading-none'>
            {title}
          </span>
          {subtitle && (
            <span className='block mt-2 text-xs text-muted-foreground leading-relaxed'>
              {subtitle}
            </span>
          )}
        </div>
        {children}
        {source && (
          <figcaption className='mt-5 pt-4 border-t border-border/60 text-[11px] text-muted-foreground leading-relaxed'>
            {source}
          </figcaption>
        )}
      </div>
    </figure>
  )
}

/** The human's square: the "H" chip the hero instrument uses for direction. */
function HumanChip({ size = 'md' }: { size?: 'md' | 'lg' }) {
  const box = size === 'lg' ? 'w-12 h-12 text-sm' : 'w-10 h-10 text-xs'
  return (
    <span
      className={`inline-grid place-items-center ${box} bg-foreground text-background font-mono font-bold leading-none`}
      aria-hidden='true'
    >
      H
    </span>
  )
}

function Badge({ color, children }: { color: string; children: string }) {
  return (
    <span
      className='inline-block font-mono text-[10px] font-semibold tracking-[0.14em] uppercase px-2 py-1 leading-none'
      style={{ backgroundColor: `${color}15`, color }}
    >
      {children}
    </span>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 1. The loop: five phases, one finite motion                               */
/* ────────────────────────────────────────────────────────────────────────── */

const PHASES = [
  {
    index: '00',
    title: 'Set the constraint',
    who: 'Human direction',
    color: YELLOW,
    body: 'A person states the question the system is allowed to work on.',
    out: 'One explicit question',
    Mark: () => <HumanChip />,
  },
  {
    index: '01',
    title: 'Radar sweeps',
    who: 'Measure',
    color: BLUE,
    body: 'Audit how AI engines read the brand against that question.',
    out: '13 checks, each with a fix',
    Mark: () => <RadarMark className='w-10 h-10' />,
  },
  {
    index: '02',
    title: 'Vector points',
    who: 'Qualify',
    color: PURPLE,
    body: 'Turn the evidence into a scored, explained next move.',
    out: '12 dimensions, one route',
    Mark: () => <VectorMark className='w-10 h-10' />,
  },
  {
    index: '03',
    title: 'Hive multiplies',
    who: 'Build',
    color: PINK,
    body: 'Coordinate AI workers around the chosen route, in stages.',
    out: 'Workers with visible handoffs',
    Mark: () => <HiveMark className='w-10 h-10' />,
  },
  {
    index: '04',
    title: 'Review. Approve. Redirect.',
    who: 'Return to human',
    color: GREEN,
    body: 'The motion stops where ownership sits: with your team.',
    out: 'A decision, then a new question',
    Mark: () => (
      <span
        className='inline-grid place-items-center w-10 h-10 border border-border/60 bg-background text-foreground'
        aria-hidden='true'
      >
        <Check size={20} />
      </span>
    ),
  },
]

export function DirectedGridLoopDiagram() {
  return (
    <Frame
      title='The Directed Grid: one finite motion'
      subtitle='The same five phases the homepage instrument animates. It starts with a question and holds on a human decision. It does not run forever.'
    >
      <ol className='list-none m-0 p-0 grid grid-cols-1 md:grid-cols-5 gap-3'>
        {PHASES.map((phase, i) => (
          <li key={phase.index} className='relative m-0 p-0'>
            <div className='h-full bg-background border border-border/60 p-4 flex flex-col gap-3'>
              <div className='flex items-center justify-between gap-2'>
                <phase.Mark />
                <span className='font-mono text-[10px] text-muted-foreground tracking-[0.14em]'>
                  {phase.index}
                </span>
              </div>
              <Badge color={phase.color}>{phase.who}</Badge>
              <span className='block text-sm font-semibold text-foreground leading-snug'>
                {phase.title}
              </span>
              <span className='block text-xs text-foreground/80 leading-relaxed'>
                {phase.body}
              </span>
              <span className='block mt-auto pt-3 border-t border-border/60 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground leading-snug'>
                Out: {phase.out}
              </span>
            </div>
            {i < PHASES.length - 1 && (
              <span
                className='hidden md:grid absolute top-1/2 -right-[14px] -translate-y-1/2 z-10 place-items-center w-6 h-6 bg-muted text-muted-foreground'
                aria-hidden='true'
              >
                <ArrowRight size={14} />
              </span>
            )}
          </li>
        ))}
      </ol>
      <div className='mt-4 flex items-center gap-3 bg-[#6bc95a]/5 border border-[#6bc95a]/20 px-4 py-3'>
        <RotateCcw size={16} className='shrink-0' style={{ color: GREEN }} />
        <span className='text-xs text-foreground/80 leading-relaxed'>
          The checkpoint feeds the next constraint. Every loop starts with a
          human and ends with a human; the three verbs in between are bounded.
        </span>
      </div>
    </Frame>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 2. One grid, three verbs                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

const VERBS = [
  {
    name: 'Radar',
    verb: 'Measure',
    color: BLUE,
    Mark: RadarMark,
    input: 'A domain and the buyer question it should win',
    output: 'A defended score, 13 checks, a fix prompt per check',
    number: '13',
    numberLabel: 'checks in one pass',
    keeps: 'Which fixes to ship, and in what order',
  },
  {
    name: 'Vector',
    verb: 'Qualify',
    color: PURPLE,
    Mark: VectorMark,
    input: 'Every inbound conversation, your ICP and CRM',
    output: 'A 12-dimension score, the reasoning, a routed next move',
    number: '12',
    numberLabel: 'scoring dimensions',
    keeps: 'Authority over the route; unclear fit goes to a person',
  },
  {
    name: 'Hive',
    verb: 'Build',
    color: PINK,
    Mark: HiveMark,
    input: 'The approved route and the systems it touches',
    output: 'Coordinated AI workers, staged releases, traces',
    number: '12',
    numberLabel: 'weeks, four controlled releases',
    keeps: 'Escalations, with the context intact',
  },
]

export function DirectedGridVerbsDiagram() {
  return (
    <Frame
      title='One grid, three verbs'
      subtitle='Each product is one verb on the same 64x64 grid. The pink cell is the verb; the neutral cells are what stays constant. What goes in, what comes out, and what the human keeps.'
      source='Numbers are product facts as of August 2026: Radar runs 13 checks per audit; Vector scores 12 dimensions and deploys in 60 days; Hive ships in 12 weeks across four controlled releases.'
    >
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
        {VERBS.map(v => (
          <div
            key={v.name}
            className='bg-background border border-border/60 p-4 flex flex-col gap-3'
          >
            <div className='flex items-center gap-3'>
              <v.Mark className='w-12 h-12 shrink-0' />
              <span className='flex flex-col gap-1'>
                <span className='text-sm font-semibold text-foreground leading-none'>
                  {v.name}
                </span>
                <Badge color={v.color}>{v.verb}</Badge>
              </span>
            </div>
            <div className='flex items-baseline gap-2 pt-2 border-t border-border/60'>
              <span
                className='text-3xl font-bold leading-none tracking-tight'
                style={{ color: v.color }}
              >
                {v.number}
              </span>
              <span className='font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground leading-snug'>
                {v.numberLabel}
              </span>
            </div>
            <dl className='m-0 grid gap-2 text-xs'>
              <div>
                <dt className='font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground'>
                  In
                </dt>
                <dd className='m-0 text-foreground/80 leading-relaxed'>
                  {v.input}
                </dd>
              </div>
              <div>
                <dt className='font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground'>
                  Out
                </dt>
                <dd className='m-0 text-foreground/80 leading-relaxed'>
                  {v.output}
                </dd>
              </div>
              <div className='pt-2 border-t border-border/60'>
                <dt className='font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground'>
                  Human keeps
                </dt>
                <dd className='m-0 text-foreground leading-relaxed'>
                  {v.keeps}
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    </Frame>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 3. The autonomy ladder, and where the grid stops climbing                 */
/* ────────────────────────────────────────────────────────────────────────── */

const LEVELS = [
  {
    stars: 0,
    name: 'Simple processor',
    role: 'Model has no impact on program flow',
    control: 'Human',
  },
  {
    stars: 1,
    name: 'Router',
    role: 'Model determines basic program flow',
    control: 'Human decides what and how; model decides when',
  },
  {
    stars: 2,
    name: 'Tool caller',
    role: 'Model determines how functions are executed',
    control: 'Human decides what; model decides how',
  },
  {
    stars: 3,
    name: 'Multi-step agent',
    role: 'Model controls iteration and program continuation',
    control:
      'Human decides what functions exist; model decides which, when, how',
  },
  {
    stars: 4,
    name: 'Fully autonomous agent',
    role: 'Model creates and executes new code',
    control: 'System',
  },
]

const PLACEMENT: Record<
  number,
  {
    Mark: React.ComponentType<{ className?: string }>
    name: string
    note: string
  }[]
> = {
  2: [
    {
      Mark: RadarMark,
      name: 'Radar',
      note: 'Runs a fixed set of 13 checks against one question',
    },
  ],
  3: [
    {
      Mark: VectorMark,
      name: 'Vector',
      note: 'Scores and routes inside a rubric you approved; people keep authority',
    },
    {
      Mark: HiveMark,
      name: 'Hive',
      note: 'Iterates in staged releases with visible handoffs and escalation',
    },
  ],
}

export function AutonomyLadderDiagram() {
  return (
    <Frame
      title='The autonomy ladder, and where the grid stops climbing'
      subtitle='Five levels of AI agent, from a model with no say over program flow to one that writes and runs its own code. The Directed Grid deliberately never reaches the top rung.'
      source='Levels and control column after Table 1 in Mitchell, Ghosh, Luccioni and Pistilli, "Fully Autonomous AI Agents Should Not be Developed" (2025). Product placement is Pixelmojo analysis, not the paper.'
    >
      <ol className='list-none m-0 p-0 grid gap-2'>
        {LEVELS.map(level => {
          const placed = PLACEMENT[level.stars]
          const top = level.stars === 4
          return (
            <li
              key={level.name}
              className={`m-0 p-0 border ${top ? 'border-[#F90B8A]/20 bg-[#F90B8A]/5' : 'border-border/60 bg-background'}`}
            >
              <div className='grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 items-center p-3 sm:p-4'>
                <span
                  className='font-mono text-xs tracking-[0.2em] leading-none'
                  style={{ color: top ? PINK : BLUE }}
                  aria-label={`${level.stars} of 4`}
                >
                  {'★'.repeat(level.stars)}
                  <span className='text-border'>
                    {'☆'.repeat(4 - level.stars)}
                  </span>
                </span>
                <span className='flex flex-col gap-1 min-w-0'>
                  <span className='text-sm font-semibold text-foreground leading-none'>
                    {level.name}
                  </span>
                  <span className='text-xs text-foreground/80 leading-relaxed'>
                    {level.role}
                  </span>
                </span>
                <span className='col-span-2 sm:col-span-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground leading-snug sm:text-right sm:max-w-[14rem]'>
                  Control: {level.control}
                </span>
              </div>
              {placed && (
                <div className='border-t border-border/60 px-3 sm:px-4 py-3 grid gap-2'>
                  {placed.map(p => (
                    <span key={p.name} className='flex items-center gap-3'>
                      <p.Mark className='w-6 h-6 shrink-0' />
                      <span className='text-xs leading-relaxed'>
                        <span className='font-semibold text-foreground'>
                          {p.name}
                        </span>{' '}
                        <span className='text-foreground/80'>{p.note}</span>
                      </span>
                    </span>
                  ))}
                </div>
              )}
              {top && (
                <div className='border-t border-[#F90B8A]/20 px-3 sm:px-4 py-3 flex items-center gap-3'>
                  <ShieldCheck
                    size={16}
                    className='shrink-0'
                    style={{ color: PINK }}
                  />
                  <span className='text-xs text-foreground/80 leading-relaxed'>
                    Nothing Pixelmojo ships lives here. The grid returns to a
                    human before any verb can redefine its own task.
                  </span>
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </Frame>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 4. What a human checkpoint needs                                          */
/* ────────────────────────────────────────────────────────────────────────── */

const ARRIVES = [
  {
    label: 'Evidence',
    color: BLUE,
    body: 'What the verb actually found. A Radar check with its fix. A Vector score with its reasoning. A Hive trace of what each worker did.',
  },
  {
    label: 'Confidence',
    color: PURPLE,
    body: 'How sure the system is, and the rule that decides when it is not sure enough to act alone.',
  },
  {
    label: 'Recommended action',
    color: PINK,
    body: 'One proposed next move, stated plainly enough that a person can accept it or overrule it in a minute.',
  },
]

const DECIDES = [
  { verb: 'Review', body: 'Read the evidence; ask for more before acting.' },
  { verb: 'Approve', body: 'Let the recommended move ship as proposed.' },
  { verb: 'Redirect', body: 'Change the constraint and start the loop again.' },
]

export function HumanCheckpointDiagram() {
  return (
    <Frame
      title='What a human checkpoint needs'
      subtitle='A checkpoint is only real if the person has enough to decide. Three things have to arrive; three decisions have to be available.'
    >
      <div className='grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch'>
        <div className='grid gap-2'>
          <span className='font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground'>
            What arrives
          </span>
          {ARRIVES.map(a => (
            <div
              key={a.label}
              className='bg-background border border-border/60 p-4'
            >
              <Badge color={a.color}>{a.label}</Badge>
              <span className='block mt-2 text-xs text-foreground/80 leading-relaxed'>
                {a.body}
              </span>
            </div>
          ))}
        </div>
        <div className='hidden md:grid place-items-center px-1'>
          <HumanChip size='lg' />
        </div>
        <div className='grid gap-2'>
          <span className='font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground'>
            What the human decides
          </span>
          {DECIDES.map(d => (
            <div
              key={d.verb}
              className='bg-background border border-border/60 p-4 flex items-start gap-3'
            >
              <span
                className='inline-grid place-items-center w-7 h-7 shrink-0 bg-foreground text-background'
                aria-hidden='true'
              >
                <Check size={14} />
              </span>
              <span className='min-w-0'>
                <span className='block text-sm font-semibold text-foreground leading-none'>
                  {d.verb}
                </span>
                <span className='block mt-1.5 text-xs text-foreground/80 leading-relaxed'>
                  {d.body}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  )
}
